import { ShieldCheck, FileText, RefreshCw, CheckCircle2, AlertTriangle, ClipboardList, Scale } from "lucide-react";
import { Reveal, SectionHeading } from "../components/primitives";
import { PageHero, CTASection, BookButtons } from "../components/sections";

const SECTIONS = [
  {
    icon: CheckCircle2,
    title: "Eligibility",
    text: "The money-back guarantee applies to students who enrol in DWC's qualifying guaranteed training program. Eligibility is confirmed in writing at the time of enrolment — ask the team for the current qualifying program structure before you begin.",
  },
  {
    icon: ClipboardList,
    title: "Required Training",
    text: "The guarantee covers students who complete the full qualifying training path — Learning DNA, Reaction DNA and Confidence DNA — with regular attendance and completion of all prescribed sessions.",
  },
  {
    icon: FileText,
    title: "Program Completion",
    text: "Completion means attending every scheduled session, following the instructor's training plan, and participating in the final road assessment. Missed or incomplete sessions may affect eligibility.",
  },
  {
    icon: Scale,
    title: "Assessment Criteria",
    text: "After completing the qualifying program, driving ability is evaluated through a structured road assessment. If a student who has genuinely completed the full program still cannot drive, they may qualify for the refund.",
  },
  {
    icon: AlertTriangle,
    title: "Exclusions",
    text: "The guarantee does not apply to incomplete programs, irregular attendance, refusal to follow the training plan, or medical/licensing factors outside DWC's control. Specific exclusions are listed in the written terms provided at enrolment.",
  },
  {
    icon: RefreshCw,
    title: "Refund Process",
    text: "Eligible claims are raised directly with the DWC team, reviewed against the written terms, and processed as a full refund of the qualifying program fee. DWC was the first driving school in India to offer this guarantee in writing.",
  },
];

const Guarantee = () => (
  <>
    <PageHero
      eyebrow="The DWC Guarantee"
      lines={["100% Money-Back", "Driving Guarantee."]}
      text="We believe in our training. That's why eligible students who complete the qualifying guaranteed program may be covered by our money-back guarantee — subject to the program terms and conditions."
    >
      <BookButtons bookTestId="guarantee-book-btn" waTestId="guarantee-whatsapp-btn" />
    </PageHero>

    <section className="bg-white py-24 md:py-32" data-testid="guarantee-details">
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <div className="flex items-start gap-6">
          <span className="hidden h-20 w-20 shrink-0 items-center justify-center rounded-3xl bg-dwc-green text-white ring-4 ring-dwc-crimson/30 md:flex">
            <ShieldCheck className="h-10 w-10" aria-hidden="true" />
          </span>
          <SectionHeading
            eyebrow="Terms & Conditions"
            number="01"
            title="How The Guarantee Works"
            text="The guarantee is a written commitment, not a marketing line. Below is how it works — the detailed written terms are shared with every student at enrolment, and the enrolment copy prevails."
          />
        </div>
        <div className="mt-14 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {SECTIONS.map((s, i) => (
            <Reveal key={s.title} delay={(i % 3) * 0.08} className="h-full">
              <article className="h-full rounded-2xl border border-gray-100 bg-white p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)] transition-[box-shadow,transform] duration-300 hover:-translate-y-1 hover:shadow-[0_20px_50px_rgb(23,107,58,0.10)]">
                <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-dwc-soft text-dwc-green">
                  <s.icon className="h-5 w-5" aria-hidden="true" />
                </span>
                <h3 className="mt-4 text-base font-extrabold tracking-tight text-dwc-charcoal">{s.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-dwc-charcoal/65">{s.text}</p>
              </article>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.15}>
          <p className="mt-12 rounded-2xl border border-dwc-crimson/20 bg-dwc-crimson/[0.03] p-6 text-sm leading-relaxed text-dwc-charcoal/70" data-testid="guarantee-disclaimer">
            <strong className="text-dwc-crimson">Important:</strong> This page is a summary. The complete, legally binding terms and conditions of the 100% Money-Back Driving Guarantee are provided in writing at the time of enrolment. Please read them carefully and ask the DWC team any questions before enrolling. Note that failing the RTO driving test does <strong>not</strong> automatically trigger a refund — the guarantee is assessed against the written program terms.
          </p>
        </Reveal>
      </div>
    </section>

    <section className="bg-dwc-soft py-24 md:py-32" data-testid="guarantee-distinctions">
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <SectionHeading
          eyebrow="Know The Difference"
          number="02"
          title="The Guarantee Is Not The Only Promise — But It Is A Separate One."
          text="Students sometimes mix the guarantee with DWC's other commitments. Here is how they differ."
        />
        <div className="mt-14 grid gap-6 md:grid-cols-3">
          {[
            {
              title: "Money-Back Guarantee",
              text: "A written refund commitment for eligible students who complete the qualifying guaranteed program and still cannot drive — assessed against signed terms. It is not an ordinary cancellation or refund policy, and RTO test failure alone does not trigger it.",
              tone: "green",
            },
            {
              title: "Lifetime Training Support",
              text: "A separate benefit of the instructor-recommended program: continuing training support under applicable DWC terms — not a fixed 7-day or 15-day expiry. Genuine pauses (vacation, travel abroad, pregnancy and similar circumstances, informed to DWC) place the program on standby to continue later. If you change your car later, support can continue under the original paid program as per terms.",
              tone: "charcoal",
            },
            {
              title: "Own-Car Training",
              text: "A training model, not a guarantee: all training happens in your own car — Manual, Automatic or EV, any brand. Your own suitable car is mandatory; DWC does not provide an alternative school car for driving training.",
              tone: "white",
            },
          ].map((c, i) => (
            <Reveal key={c.title} delay={i * 0.1} className="h-full">
              <article data-testid={`guarantee-distinction-${i}`} className={`h-full rounded-2xl p-8 ${c.tone === "green" ? "bg-dwc-green text-white grain" : c.tone === "charcoal" ? "bg-dwc-charcoal text-white grain" : "border border-gray-100 bg-white shadow-[0_8px_30px_rgb(0,0,0,0.04)]"}`}>
                <p className={`text-xs font-black tracking-[0.2em] ${c.tone === "white" ? "text-dwc-crimson" : "text-emerald-300"}`}>{String(i + 1).padStart(2, "0")}</p>
                <h3 className={`mt-3 text-lg font-extrabold tracking-tight ${c.tone === "white" ? "text-dwc-charcoal" : "text-white"}`}>{c.title}</h3>
                <p className={`mt-3 text-sm leading-relaxed ${c.tone === "white" ? "text-dwc-charcoal/70" : "text-white/80"}`}>{c.text}</p>
              </article>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.2}>
          <div className="mt-12 rounded-2xl border border-dwc-green/15 bg-white p-8" data-testid="guarantee-abandonment-note">
            <h3 className="text-base font-extrabold tracking-tight text-dwc-charcoal">What if training is paused or left incomplete?</h3>
            <p className="mt-3 text-sm leading-relaxed text-dwc-charcoal/70">
              If a student voluntarily abandons the program permanently, the guarantee does not automatically apply. If an approved break or standby exists (informed to DWC for a genuine reason), eligibility can continue according to the applicable terms. And if a student completes the recommended program and, after DWC assessment, still has not reached the required confidence level, additional support and training continue under the lifetime-support structure — with refund eligibility reviewed under the applicable written terms.
            </p>
          </div>
        </Reveal>
      </div>
    </section>

    <CTASection title="Train With Zero Risk On Your Mind." sub="Ask about the guaranteed program during your appointment." chapter="03" />
  </>
);

export default Guarantee;
